// ═══════════════════════════════════════════════════════════════════════
// tenant-brand.ts — the single source of truth for the white-label brand
// name shown anywhere in the live tenant OS runtime.
// ═══════════════════════════════════════════════════════════════════════
// The tenant runtime has no client-side operator resolution; the loaded OS's
// own name IS the brand. Priority: the loaded os_name → a neutral fallback.
// This NEVER returns a "Sloe" string — full white-label. Every visible surface
// (the document title, etc.) reads from here instead of hardcoding a fallback.
// ═══════════════════════════════════════════════════════════════════════

import { useMemo } from "react";

export const NEUTRAL_BRAND = "Your Operating System";

export interface TenantBrand {
  name: string;
  shortName: string;
}

function toShortName(name: string): string {
  const n = name.trim();
  return n.length > 12 ? n.slice(0, 12).trim() : n;
}

export function resolveTenantBrand(opts?: { osName?: string | null }): TenantBrand {
  const osName = opts?.osName?.trim();
  const name = osName || NEUTRAL_BRAND;
  return { name, shortName: toShortName(name) };
}

export function useTenantBrand(opts?: { osName?: string | null }): TenantBrand {
  const osName = opts?.osName ?? null;
  return useMemo(() => resolveTenantBrand({ osName }), [osName]);
}
